import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Confetti from "react-confetti";
import FutureIdentityCard from "../../../Identity/FutureYou/FutureIdentityCard";
import FutureValuesTransitionAnimation from "./FutureValuesTransitionAnimation";
import stars from "../../../../../../../assets/stars.png";

const FutureValuesCompletion = ({ 
  top3Values, 
  futureData = {},
  userName = "User",
  profileImage = null,
  onNext 
}) => {
  const [showConfetti, setShowConfetti] = useState(true);
  const [showValuesCard, setShowValuesCard] = useState(true);
  const [showTransitionAnimation, setShowTransitionAnimation] = useState(false);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const confettiTimer = setTimeout(() => {
      setShowConfetti(false);
      setShowValuesCard(false);
      setShowTransitionAnimation(true);
    }, 5000);

    return () => clearTimeout(confettiTimer);
  }, []);

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleAnimationComplete = () => {
    setShowTransitionAnimation(false);
    setShowProfileCard(true);
  };

  // ✅ AUTO-NAVIGATE to FinalResult after 2 seconds when ProfileCard shows
  useEffect(() => {
    if (showProfileCard) {
      const autoNavigateTimer = setTimeout(() => {
        onNext?.();
      }, 2000); // 2 seconds

      return () => clearTimeout(autoNavigateTimer);
    }
  }, [showProfileCard, onNext]);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-white overflow-y-auto">
  
      {/* Confetti */}
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          numberOfPieces={250}
          gravity={0.3}
          recycle={false}
          colors={["#FFD700", "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7"]}
        />
      )}

      {/* FIXED TITLE AT TOP */}
      {!showProfileCard && (
        <div 
          className="absolute top-16 left-0 right-0 z-[10000]"
          style={{ pointerEvents: "none" }}
        >
          <div className="text-center">
            <h1
              style={{
                fontFamily: "Gotham",
                fontWeight: 500,
                fontSize: 32,
                lineHeight: "135%",
                letterSpacing: "0%",
                color: "#323232",
              }}
            >
              Well done! Your future values are set.
            </h1>
          </div>
        </div>
      )}

      {/* Main Content - Centered vertically */}
      <div className="relative z-10 w-full h-full flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-[429px]">
          {/* Phase 1: Values Card (0-5s) */}
          {showValuesCard && (
            <div
              style={{
                width: 381,
                minHeight: 438,
                borderRadius: 22.2,
                border: "0.89px solid",
                borderImageSource: "linear-gradient(161.13deg, #D9D9D9 -6.67%, #707070 90.21%)",
                background: "#FFFFFF",
                boxShadow: "0px 8.88px 65.72px 0px rgba(0, 0, 0, 0.2)",
              }}
              className="overflow-hidden flex flex-col items-center mx-auto relative"
            >
              {/* Header */}
                <div
                  style={{
                    width: "100%",
                    height: 118,
                    background: "#0D1111",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                  }}
                >
                  {/* LEFT STARS - Rotated */}
                  <img
                    src={stars}
                    alt="Stars"
                    style={{
                      width: "24px",
                      height: "24px",
                      transform: "rotate(180deg)",
                      opacity: 0.9,
                    }}
                  />

  {/* CENTER TITLE */}
  <h2
    style={{
      fontFamily: "Gotham",
      fontWeight: 700,
      fontSize: 23.33,
      lineHeight: "120%",
      letterSpacing: "0%",
      textTransform: "uppercase",
      color: "#FFFFFF",
      textAlign: "center",
      whiteSpace: "nowrap",
    }}
  >
    Your Future Values
  </h2>

  {/* RIGHT STARS - Normal */}
  <img
    src={stars}
    alt="Stars"
    style={{
      width: "24px",
      height: "24px",
      opacity: 0.9,
    }}
  />
</div>


              {/* Top 3 Values in card */}
              <div className="flex flex-col items-center justify-center py-8 gap-6 px-4">
                {top3Values.map((value, index) => (
                  <div
                    key={index}
                    style={{
                      width: 258,
                      height: 69,
                      borderRadius: 50,
                      border: "1px solid #CCCCCC",
                      background: "#54B0AF",
                      paddingTop: 17,
                      paddingBottom: 17,
                      paddingLeft: 46,
                      paddingRight: 46,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "Gotham",
                        fontWeight: 700,
                        fontSize: 20,
                        lineHeight: "160%",
                        letterSpacing: "0%",
                        color: "#FFFFFF",
                        textAlign: "center",
                      }}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phase 3: Future Identity Card (Shows for 2s then auto-navigates) */}
          {showProfileCard && (
            <div
              style={{
                animation: "fadeIn 0.8s ease-in-out",
              }}
            >
              <FutureIdentityCard
                futureData={futureData}
                userName={userName}
                profileImage={futureData?.profileImage || profileImage}
                currentInput=""
                coreValues={top3Values}
                showCoreValues={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Phase 2: Transition Animation */}
      {showTransitionAnimation && (
        <FutureValuesTransitionAnimation
          values={top3Values}
          onAnimationComplete={handleAnimationComplete}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default FutureValuesCompletion;
